import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PropostaComercial } from '@/features/propostas/types/propostaComercial';

export class PDFService {
  /**
   * Gera PDF de uma proposta comercial com layout profissional
   */
  static async gerarPDFProposta(proposta: PropostaComercial): Promise<Blob> {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPosition = margin;

    // Cores do tema
    const primaryColor = [41, 98, 255]; // Azul profissional
    const secondaryColor = [100, 116, 139]; // Cinza azulado
    const accentColor = [16, 185, 129]; // Verde para destaques positivos
    const lightGray = [248, 250, 252];
    const darkGray = [30, 41, 59];

    // Configurações de fonte
    const titleFontSize = 22;
    const normalFontSize = 11;
    const smallFontSize = 9;

    // ============ CABEÇALHO ELEGANTE ============
    // Faixa superior colorida
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.rect(0, 0, pageWidth, 35, 'F');

    // Título da proposta em branco
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(titleFontSize);
    doc.setFont('helvetica', 'bold');
    doc.text(proposta.titulo, margin, 15);

    // Subtítulo
    doc.setFontSize(smallFontSize);
    doc.setFont('helvetica', 'normal');
    doc.text('PROPOSTA COMERCIAL DE CONSÓRCIO', margin, 23);

    // Data e Status no canto direito
    doc.setFontSize(smallFontSize);
    const dataText = `Criada em: ${proposta.data_criacao.toLocaleDateString('pt-BR')}`;
    const validadeText = proposta.data_validade ? `Válida até: ${proposta.data_validade.toLocaleDateString('pt-BR')}` : '';
    const statusText = `Status: ${proposta.status}`;

    doc.text(dataText, pageWidth - margin - doc.getTextWidth(dataText), 13);
    if (validadeText) {
      doc.text(validadeText, pageWidth - margin - doc.getTextWidth(validadeText), 19);
    }
    doc.text(statusText, pageWidth - margin - doc.getTextWidth(statusText), validadeText ? 25 : 21);

    yPosition = 45;

    // Resetar cor do texto
    doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);

    // ============ DADOS DO CLIENTE ============
    this.drawSectionHeader(doc, 'DADOS DO CLIENTE', margin, yPosition, contentWidth, primaryColor);
    yPosition += 12;

    // Card com fundo claro
    doc.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
    doc.roundedRect(margin, yPosition, contentWidth, 12, 2, 2, 'F');

    doc.setFontSize(normalFontSize);
    doc.setFont('helvetica', 'bold');
    doc.text('Nome:', margin + 3, yPosition + 8);
    doc.setFont('helvetica', 'normal');
    doc.text(proposta.cliente_nome, margin + 20, yPosition + 8);
    yPosition += 20;

    // ============ GRUPO DE CONSÓRCIO ============
    this.drawSectionHeader(doc, 'GRUPO DE CONSÓRCIO', margin, yPosition, contentWidth, primaryColor);
    yPosition += 12;

    if (proposta.grupo) {
      // Grid de informações do grupo
      const grupoInfo = [
        { label: 'Número', value: proposta.grupo.numero_grupo.toString() },
        { label: 'Espécie', value: proposta.grupo.especie },
        { label: 'Duração', value: `${proposta.grupo.duracao_total_meses} meses` },
        { label: 'Tipo de Plano', value: proposta.grupo.tipo_plano },
      ];

      const boxWidth = (contentWidth - 6) / 2;
      const boxHeight = 18;
      let xPos = margin;
      let row = 0;

      grupoInfo.forEach((info, index) => {
        if (index % 2 === 0 && index > 0) {
          row++;
          xPos = margin;
        }

        const currentY = yPosition + (row * (boxHeight + 3));

        // Box com borda
        doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.setLineWidth(0.3);
        doc.roundedRect(xPos, currentY, boxWidth, boxHeight, 2, 2, 'S');

        // Label
        doc.setFontSize(smallFontSize);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
        doc.text(info.label, xPos + 3, currentY + 6);

        // Valor
        doc.setFontSize(normalFontSize);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.text(info.value, xPos + 3, currentY + 13);

        xPos += boxWidth + 3;
      });

      yPosition += (Math.ceil(grupoInfo.length / 2) * (boxHeight + 3)) + 10;
    }

    // ============ SIMULAÇÃO DE LANCE ============
    this.drawSectionHeader(doc, 'SIMULAÇÃO DE LANCE', margin, yPosition, contentWidth, primaryColor);
    yPosition += 12;

    const vAdicional = proposta.simulacao_lance.valor_bolso_adicional;
    const vEntrada = proposta.simulacao_lance.valor_bolso_entrada;

    // Tabela dinâmica conforme solicitado pelo usuário
    const tableData: any[] = [
      { label: 'Valor do Bem', value: proposta.simulacao_lance.valor_veiculo, highlight: false },
      { label: 'Lance Embutido', value: proposta.simulacao_lance.valor_lance, highlight: false },
    ];
    tableData.push({ label: 'Lance Adicional (Do Bolso)', value: vAdicional || 0, highlight: false });
    tableData.push({ label: 'Entrada (Pagamento Inicial)', value: vEntrada || 0, highlight: false });
    tableData.push({ label: 'Valor da Carta', value: proposta.simulacao_lance.valor_carta, highlight: false });

    tableData.push(
      { label: 'Valor Líquido a Receber', value: proposta.simulacao_lance.valor_liquido_receber || 0, highlight: true },
      { label: 'Nova Parcela Pós-Contemplação', value: proposta.simulacao_lance.nova_parcela_pos_contemplacao || 0, highlight: true },
    );

    const prazoOriginal = proposta.grupo?.duracao_total_meses || 120;
    const prazoFinal = proposta.simulacao_lance.novo_prazo || prazoOriginal;

    tableData.push({ 
      label: 'Prazo Restante p/ Quitação', 
      value: `${prazoFinal} parcelas`, 
      highlight: true,
      type: 'text' as const
    });

    this.drawStyledTable(doc, tableData, margin, yPosition, contentWidth, primaryColor, accentColor, lightGray);
    yPosition += (tableData.length * 10) + 15;

    // ============ COMPARAÇÃO COM FINANCIAMENTO ============
    if (proposta.comparacao_financiamento) {
      // Verificar se há espaço suficiente
      if (yPosition > pageHeight - 120) {
        doc.addPage();
        yPosition = margin;
      }

      this.drawSectionHeader(doc, 'COMPARAÇÃO: CONSÓRCIO vs FINANCIAMENTO', margin, yPosition, contentWidth, primaryColor);
      yPosition += 12;

      const comparacaoData = [
        { label: 'Prazo Consórcio (Reduzido)', value: `${proposta.simulacao_lance.novo_prazo || proposta.grupo?.duracao_total_meses} meses`, highlight: false, type: 'text' as const },
        { label: 'Prazo Financiamento', value: `${proposta.comparacao_financiamento.numero_parcelas} meses`, highlight: false, type: 'text' as const },
        { label: 'Entrada Sugerida (Financ.)', value: proposta.comparacao_financiamento.entrada || 0, highlight: false },
        { label: 'Parcela do Financiamento', value: proposta.comparacao_financiamento.valor_parcela_financiamento, highlight: false },
        { label: 'Custo Total Financiamento', value: proposta.comparacao_financiamento.custo_total_financiamento, highlight: false },
        { label: 'Custo Total Consórcio', value: proposta.comparacao_financiamento.custo_total_consorcio, highlight: false },
        { label: 'Economia Gerada (Total)', value: proposta.comparacao_financiamento.economia_gerada, highlight: true },
      ];

      this.drawStyledTable(doc, comparacaoData, margin, yPosition, contentWidth, primaryColor, accentColor, lightGray);
      yPosition += (comparacaoData.length * 10) + 15;
    }

    // ============ OBSERVAÇÕES ============
    if (proposta.observacoes) {
      if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = margin;
      }

      this.drawSectionHeader(doc, 'OBSERVAÇÕES', margin, yPosition, contentWidth, primaryColor);
      yPosition += 12;

      // Box de observações
      doc.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
      const splitObservacoes = doc.splitTextToSize(proposta.observacoes, contentWidth - 6);
      const obsHeight = (splitObservacoes.length * 5) + 6;
      doc.roundedRect(margin, yPosition, contentWidth, obsHeight, 2, 2, 'F');

      doc.setFontSize(normalFontSize);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
      doc.text(splitObservacoes, margin + 3, yPosition + 5);
    }

    // ============ RODAPÉ PROFISSIONAL ============
    const footerY = pageHeight - 15;

    // Linha decorativa
    doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);

    doc.setFontSize(smallFontSize);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    doc.text('Esta proposta foi gerada automaticamente pelo sistema de propostas comerciais.', margin, footerY);

    const pageText = `Página 1 de ${doc.getNumberOfPages()}`;
    doc.setFont('helvetica', 'normal');
    doc.text(pageText, pageWidth - margin - doc.getTextWidth(pageText), footerY);

    return doc.output('blob');
  }

  /**
   * Desenha um cabeçalho de seção estilizado
   */
  private static drawSectionHeader(
    doc: jsPDF,
    title: string,
    x: number,
    y: number,
    width: number,
    color: number[]
  ) {
    // Barra lateral colorida
    doc.setFillColor(color[0], color[1], color[2]);
    doc.rect(x, y, 3, 8, 'F');

    // Texto do título
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(color[0], color[1], color[2]);
    doc.text(title, x + 6, y + 6);

    // Linha decorativa
    doc.setDrawColor(color[0], color[1], color[2]);
    doc.setLineWidth(0.3);
    doc.line(x, y + 10, x + width, y + 10);
  }

  /**
   * Desenha uma tabela estilizada
   */
  private static drawStyledTable(
    doc: jsPDF,
    data: Array<{ label: string; value: any; highlight: boolean; type?: 'currency' | 'text' }>,
    x: number,
    y: number,
    width: number,
    primaryColor: number[],
    accentColor: number[],
    lightGray: number[]
  ) {
    const rowHeight = 10;
    const darkGray = [30, 41, 59];

    data.forEach((row, index) => {
      const currentY = y + (index * rowHeight);
      const isHighlight = row.highlight;

      // Fundo alternado
      if (index % 2 === 0) {
        doc.setFillColor(255, 255, 255);
      } else {
        doc.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
      }
      doc.rect(x, currentY, width, rowHeight, 'F');

      // Borda esquerda para itens destacados
      if (isHighlight) {
        doc.setFillColor(accentColor[0], accentColor[1], accentColor[2]);
        doc.rect(x, currentY, 2, rowHeight, 'F');
      }

      // Label
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
      doc.text(row.label, x + (isHighlight ? 5 : 3), currentY + 6.5);

      // Valor
      doc.setFont('helvetica', 'bold');
      if (isHighlight) {
        doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
      } else {
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
      }
      const valueText = row.type === 'text' ? row.value : this.formatCurrency(row.value);
      const textWidth = doc.getTextWidth(valueText);
      doc.text(valueText, x + width - textWidth - 3, currentY + 6.5);
    });

    // Borda externa da tabela
    doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setLineWidth(0.3);
    doc.rect(x, y, width, data.length * rowHeight, 'S');
  }



  /**
   * Formata valor em moeda brasileira
   */
  private static formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Gera PDF a partir de um elemento HTML
   */
  static async gerarPDFFromHTML(elementId: string): Promise<Blob> {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error('Elemento não encontrado');
    }

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    });

    const imgData = canvas.toDataURL('image/png');
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    doc.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

    return doc.output('blob');
  }
}
